import { FC, useCallback, useState } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import LensIcon from '@mui/icons-material/Lens';

import ModalConfirmDialog from 'components/Modals/ModalConfirmDialog';
import Tooltip from 'components/Tooltip';
import { YOU_HAVE_EXCEEDED_YOUR_CREDITS } from 'constants/accountSettings';
import { STATUS_COMPLETED, STATUSES } from 'constants/documentStatuses';
import ROUTES from 'constants/routes';
import { useRolePermission } from 'hooks/useRolePermission';
import { RootStateType } from 'store/reducers';

interface IChangeStatusButtonProps {
  statusObject: {
    statuses: string[];
    selectedStatus: string;
    onSelectStatus: (status: string) => void;
  } | undefined;
  disabledButton: boolean;
}

const ChangeStatusButton: FC<IChangeStatusButtonProps> = ({ statusObject, disabledButton }) => {
  const location = useLocation();
  const isExecutedCollectionPage = location.pathname.includes(ROUTES.COLLECTIONS_EXECUTED_EDITOR);
  const { enableDocumentsAutoLock } = useRolePermission();
  const { documentsLimitLeft } = useSelector((state: RootStateType) => state.profile ?? {});
  const collectionExecutedDetails = useSelector((state: RootStateType) => state.user.collectionExecutedDetails);
  const [changeStatusObject, setChangeStatusObject] = useState<{ showModal: boolean, newStatus: string }>({
    showModal: false,
    newStatus: '',
  });

  const requiredCountToCompleteDocument = isExecutedCollectionPage ? collectionExecutedDetails?.documents?.length : 1;

  const onChangeStatus = useCallback((value: string) => {
    if (disabledButton) return;
    if (statusObject && statusObject.selectedStatus && value !== statusObject.selectedStatus) {
      statusObject.onSelectStatus(value);
    }
  }, [statusObject, disabledButton]);

  const onChangeStatusHandler = (value: string) => {
    if (statusObject?.selectedStatus === STATUSES.completed && value !== STATUSES.completed) {
      setChangeStatusObject({
        showModal: true,
        newStatus: value,
      });
    } else {
      onChangeStatus(value);
    }
  };

  const getClassesForStatus = (statusValue: string): string => (
    `material-icons me-1 mb-1 status-${statusValue.toLowerCase().replaceAll(' ', '-')}`
  );

  const closeChangeStatusModal = () => {
    setChangeStatusObject((prevState) => ({ ...prevState, showModal: false }));
  };

  const shouldBlockButton = statusObject?.selectedStatus === STATUSES.completed && enableDocumentsAutoLock;

  return statusObject && statusObject.statuses
    ? (
      <>
        <div className="dropdown px-sm-2">
          <button
            className={`btn dropdown-toggle w-100 editor-header-dropdown ${shouldBlockButton ? 'border-white' : ''}`}
            type="button"
            id="selectStatusButton"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            disabled={disabledButton || shouldBlockButton}
          >
            <span className="d-none d-sm-inline">
              <span className="status-icon">
                <LensIcon className={getClassesForStatus(statusObject.selectedStatus)} />
                {statusObject.selectedStatus}
              </span>
            </span>
          </button>
          <ul
            className={`dropdown-menu editor-header-dropdown-menu ${shouldBlockButton ? 'd-none' : ''}`}
            aria-labelledby="selectStatusButton"
          >
            {
              Object.values(statusObject.statuses).map((value) => (
                <Tooltip
                  key={`statusListItem_${value}`}
                  title={
                    value === STATUS_COMPLETED && requiredCountToCompleteDocument > documentsLimitLeft
                      ? YOU_HAVE_EXCEEDED_YOUR_CREDITS
                      : ''
                  }
                >
                  <li>
                    <button
                      type="button"
                      className="dropdown-item editor-header-dropdown-item"
                      value={value}
                      onClick={() => onChangeStatusHandler(value)}
                      disabled={
                        statusObject.selectedStatus === value
                        || (value === STATUS_COMPLETED && requiredCountToCompleteDocument > documentsLimitLeft)
                      }
                    >
                      <span className="status-icon">
                        <LensIcon className={getClassesForStatus(value)} />
                        <span>{value}</span>
                      </span>
                    </button>
                  </li>
                </Tooltip>
              ))
            }
          </ul>
        </div>
        <ModalConfirmDialog
          messageText="You are changing the status of a completed and signed document. Are you sure?"
          showDialog={changeStatusObject.showModal}
          onClose={closeChangeStatusModal}
          onConfirm={() => {
            closeChangeStatusModal();
            onChangeStatus(changeStatusObject.newStatus);
          }}
        />
      </>
    )
    : null;
};

export default ChangeStatusButton;